/** ROI manager: list, numeric geometry editing and area statistics. */
import { computed } from '@preact/signals';
import { areaStats } from '../../core/area';
import { areaRows } from '../../core/report';
import { isArea, roiBounds, type Roi } from '../../core/roi';
import { fmt, unitScale } from '../../core/units';
import { deleteRoi, duplicateRoi, patchRoi, clearRois } from '../../app/actions';
import { dragging, measurement, rois, selectedId, selectedRoi, spatial } from '../../app/store';
import { viewportApi } from '../../view/viewState';
import { copyText } from '../../app/exporters';
import { Button, NumberInput } from '../controls';
import { Icon } from '../icons';

/** Area statistics for the selected ROI (colorimetry skipped while dragging for responsiveness). */
export const selectedAreaStats = computed(() => {
  const r = selectedRoi.value;
  const m = measurement.value;
  if (!r || !m || !(isArea(r) || r.kind === 'point')) return null;
  return areaStats(r, m.plane, m.clip, dragging.value ? null : m.ctx);
});

function GeometryEditor({ r }: { r: Roi }) {
  const set = (patch: Record<string, number>) => patchRoi(r.id, patch as Partial<Roi>);
  const k = unitScale(spatial.value);
  const u = spatial.value.unit;
  const num = (label: string, key: string, value: number, opts: { min?: number; integer?: boolean } = {}) => (
    <label class="geo-field" key={key}>
      <span>{label}</span>
      <NumberInput
        value={value}
        step={opts.integer ? 1 : 0.5}
        min={opts.min}
        integer={opts.integer}
        disabled={r.locked}
        testId={`geo-${key}`}
        onChange={(v) => set({ [key]: v })}
      />
    </label>
  );
  switch (r.kind) {
    case 'line': {
      const L = Math.hypot(r.x1 - r.x0, r.y1 - r.y0);
      const ang = (Math.atan2(-(r.y1 - r.y0), r.x1 - r.x0) * 180) / Math.PI;
      return (
        <div class="geo" data-help="roi.geometry">
          {num('x₀', 'x0', r.x0)}
          {num('y₀', 'y0', r.y0)}
          {num('x₁', 'x1', r.x1)}
          {num('y₁', 'y1', r.y1)}
          {num('width', 'width', r.width, { min: 1, integer: true })}
          <label class="geo-field">
            <span>length</span>
            <NumberInput
              value={L * k}
              unit={u}
              disabled={r.locked}
              min={0}
              onChange={(v) => {
                const f = L ? v / k / L : 0;
                set({ x1: r.x0 + (r.x1 - r.x0) * f, y1: r.y0 + (r.y1 - r.y0) * f });
              }}
            />
          </label>
          <label class="geo-field">
            <span>angle</span>
            <NumberInput
              value={ang}
              unit="°"
              disabled={r.locked}
              onChange={(v) => {
                const a = (-v * Math.PI) / 180;
                set({ x1: r.x0 + L * Math.cos(a), y1: r.y0 + L * Math.sin(a) });
              }}
            />
          </label>
        </div>
      );
    }
    case 'rect':
      return (
        <div class="geo" data-help="roi.geometry">
          {num('x', 'x', r.x)}
          {num('y', 'y', r.y)}
          {num('w', 'w', r.w, { min: 0 })}
          {num('h', 'h', r.h, { min: 0 })}
        </div>
      );
    case 'ellipse':
      return (
        <div class="geo" data-help="roi.geometry">
          {num('cx', 'cx', r.cx)}
          {num('cy', 'cy', r.cy)}
          {num('rx', 'rx', r.rx, { min: 0 })}
          {num('ry', 'ry', r.ry, { min: 0 })}
        </div>
      );
    case 'point':
      return (
        <div class="geo" data-help="roi.geometry">
          {num('x', 'x', r.x)}
          {num('y', 'y', r.y)}
          {num('size', 'size', r.size, { min: 1, integer: true })}
        </div>
      );
    case 'polygon': {
      const b = roiBounds(r);
      return (
        <div class="muted small" data-help="roi.geometry">
          {r.pts.length} vertices · bounds {fmt(b.x)}, {fmt(b.y)} — {fmt(b.w)} × {fmt(b.h)} px
        </div>
      );
    }
  }
}

export function RoiPanel() {
  const list = rois.value;
  const sel = selectedRoi.value;
  const st = selectedAreaStats.value;
  const m = measurement.value;
  const sp = spatial.value;
  const rows = st ? areaRows(st, m?.unit ?? '', sp.unit, unitScale(sp)) : [];
  return (
    <div class="roi-panel">
      {list.length === 0 ? (
        <div class="muted pad">
          No ROIs yet. Use the tools in the toolbar: <kbd>L</kbd> line, <kbd>R</kbd> rectangle, <kbd>E</kbd> ellipse, <kbd>P</kbd> polygon,{' '}
          <kbd>O</kbd> point.
        </div>
      ) : (
        <ul class="roi-list" data-testid="roi-list">
          {list.map((r) => (
            <li key={r.id} class={r.id === selectedId.value ? 'sel' : ''} onClick={() => (selectedId.value = r.id)}>
              <input
                type="color"
                value={r.color}
                data-help="roi.color"
                onClick={(e) => e.stopPropagation()}
                onInput={(e) => patchRoi(r.id, { color: (e.target as HTMLInputElement).value }, false)}
              />
              <span class="roi-kind">{r.kind}</span>
              <input
                class="roi-name"
                value={r.name}
                data-help="roi.name"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => patchRoi(r.id, { name: (e.target as HTMLInputElement).value })}
              />
              <button
                type="button"
                class="icon-btn"
                data-help="roi.visible"
                onClick={(e) => (e.stopPropagation(), patchRoi(r.id, { visible: !r.visible }))}
              >
                <Icon name={r.visible ? 'eye' : 'eyeOff'} size={14} />
              </button>
              <button
                type="button"
                class="icon-btn"
                data-help="roi.lock"
                onClick={(e) => (e.stopPropagation(), patchRoi(r.id, { locked: !r.locked }))}
              >
                <Icon name={r.locked ? 'lock' : 'unlock'} size={14} />
              </button>
              <button
                type="button"
                class="icon-btn"
                data-help="roi.center"
                onClick={(e) => {
                  e.stopPropagation();
                  const b = roiBounds(r);
                  viewportApi.current?.centerOn(b.x + b.w / 2, b.y + b.h / 2);
                }}
              >
                <Icon name="fit" size={14} />
              </button>
              <button type="button" class="icon-btn" data-help="roi.duplicate" onClick={(e) => (e.stopPropagation(), duplicateRoi(r.id))}>
                <Icon name="copy" size={14} />
              </button>
              <button type="button" class="icon-btn danger" data-help="roi.delete" onClick={(e) => (e.stopPropagation(), deleteRoi(r.id))}>
                <Icon name="trash" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {list.length > 1 && (
        <div class="inline right">
          <Button small kind="ghost" help="roi.clear" onClick={clearRois}>
            Delete all
          </Button>
        </div>
      )}
      {sel && (
        <div class="roi-detail">
          <div class="subhead">
            <b style={{ color: sel.color }}>{sel.name}</b> geometry (image pixels)
          </div>
          <GeometryEditor r={sel} />
          {st && (
            <>
              <div class="subhead">
                Statistics
                <span class="spacer" />
                <Button
                  small
                  icon="copy"
                  help="roi.copyStats"
                  onClick={() =>
                    copyText(
                      rows.map((r) => [r[0], typeof r[1] === 'number' ? String(r[1]) : (r[1] ?? ''), r[2]].join('\t')).join('\n'),
                      'Statistics copied',
                    )
                  }
                >
                  Copy
                </Button>
              </div>
              <table class="metrics" data-testid="area-stats">
                <tbody>
                  {rows.map(([label, value, unit]) => (
                    <tr key={label}>
                      <th>{label}</th>
                      <td class="mono">{typeof value === 'number' ? fmt(value, 6) : (value ?? '—')}</td>
                      <td class="unit">{unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
